import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    SelectChangeEvent,
    Typography,
    Paper
} from '@mui/material';
import { customersApi } from 'src/api/endpoints/customers';
import { format, parseISO } from 'date-fns';

interface CreateKycModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    userId: string;
}

const DOCUMENT_TYPES = {
    pid: [
        { value: 'aadhar', label: 'Aadhar Card' },
        { value: 'passport', label: 'Passport' },
        { value: 'driving_license', label: 'Driving License' }
    ],
    tid: [
        { value: 'pan', label: 'PAN Card' }
    ]
};

interface KycFormData {
    pid: {
        document_name: string;
        document_number: string;
    };
    tid: {
        document_name: string;
        document_number: string;
    };
    common: {
        dateOfBirth: string;
        Nationality: string;
        nameOnCard: string;
    };
}

const CreateKycModal: React.FC<CreateKycModalProps> = ({
    open,
    onClose,
    onSuccess,
    userId
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState<KycFormData>({
        pid: {
            document_name: '',
            document_number: ''
        },
        tid: {
            document_name: 'pan',
            document_number: ''
        },
        common: {
            dateOfBirth: '',
            Nationality: '',
            nameOnCard: ''
        }
    });

    const handleChange = (section: keyof KycFormData, field: string) => (
        event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent
    ) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const formatDate = (dateString: string) => {
        try {
            const date = parseISO(dateString);
            return format(date, 'dd-MM-yyyy');
        } catch (e) {
            return dateString;
        }
    };

    const validateForm = () => {
        if (!formData.common.nameOnCard || !formData.common.dateOfBirth || !formData.common.Nationality) {
            setError('Please fill in all personal details');
            return false;
        }
        if (!formData.pid.document_name || !formData.pid.document_number) {
            setError('Please fill in all Personal ID details');
            return false;
        }
        if (!formData.tid.document_number) {
            setError('Please fill in PAN Card details');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!validateForm()) {
                return;
            }

            const kycData = {
                user_id: userId,
                kyc: [
                    {
                        document_type: 'pid',
                        document_name: formData.pid.document_name,
                        document_number: formData.pid.document_number,
                        dateOfBirth: formatDate(formData.common.dateOfBirth),
                        Nationality: formData.common.Nationality,
                        nameOnCard: formData.common.nameOnCard
                    },
                    {
                        document_type: 'tid',
                        document_name: 'pan',
                        document_number: formData.tid.document_number,
                        dateOfBirth: formatDate(formData.common.dateOfBirth),
                        Nationality: formData.common.Nationality,
                        nameOnCard: formData.common.nameOnCard
                    }
                ]
            };

            const response = await customersApi.createKyc(kycData);
            
            if (response.status) {
                onSuccess();
                onClose();
            } else {
                setError(response.message || 'Failed to create KYC');
            }
        } catch (err) {
            setError('An error occurred while creating KYC');
            console.error('Error creating KYC:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Add KYC Information</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {/* Personal Details Section */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Personal Details</Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="Full Name"
                                value={formData.common.nameOnCard}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange('common', 'nameOnCard')(event)}
                                fullWidth
                                required
                                error={!!error && !formData.common.nameOnCard}
                                helperText={error && !formData.common.nameOnCard ? 'Full name is required' : ''}
                            />

                            <TextField
                                label="Date of Birth"
                                type="date"
                                value={formData.common.dateOfBirth}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange('common', 'dateOfBirth')(event)}
                                fullWidth
                                required
                                error={!!error && !formData.common.dateOfBirth}
                                helperText={error && !formData.common.dateOfBirth ? 'Date of birth is required' : ''}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />

                            <TextField
                                label="Nationality"
                                value={formData.common.Nationality}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange('common', 'Nationality')(event)}
                                fullWidth
                                required
                                error={!!error && !formData.common.Nationality}
                                helperText={error && !formData.common.Nationality ? 'Nationality is required' : ''}
                            />
                        </Stack>
                    </Paper>

                    {/* Personal ID Section */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Personal ID Details</Typography>
                        <Stack spacing={2}>
                            <FormControl fullWidth required error={!!error && !formData.pid.document_name}>
                                <InputLabel>Document Type</InputLabel>
                                <Select
                                    value={formData.pid.document_name}
                                    onChange={(event: SelectChangeEvent) => handleChange('pid', 'document_name')(event) }
                                    label="Document Type"
                                >
                                    {DOCUMENT_TYPES.pid.map((doc) => (
                                        <MenuItem key={doc.value} value={doc.value}>
                                            {doc.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {error && !formData.pid.document_name && (
                                    <FormHelperText>Document type is required</FormHelperText>
                                )}
                            </FormControl>

                            <TextField
                                label="Document Number"
                                value={formData.pid.document_number}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange('pid', 'document_number')(event)}
                                fullWidth
                                required
                                error={!!error && !formData.pid.document_number}
                                helperText={error && !formData.pid.document_number ? 'Document number is required' : ''}
                            />
                        </Stack>
                    </Paper>

                    {/* Tax ID Section */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Tax ID Details (PAN Card)</Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="PAN Number"
                                value={formData.tid.document_number}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange('tid', 'document_number')(event)}
                                fullWidth
                                required
                                error={!!error && !formData.tid.document_number}
                                helperText={error && !formData.tid.document_number ? 'PAN number is required' : ''}
                            />
                        </Stack>
                    </Paper>

                    {error && (
                        <FormHelperText error>
                            {error}
                        </FormHelperText>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? 'Creating...' : 'Create KYC'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateKycModal; 